"use server";

import { validateParams } from "@/lib/utils";
import z from "zod";
import { ActionResult } from "@/types/server-action";
import { auth } from "@/auth";
import { resolveVehicleIdentificationSearch } from "@/lib/cat/vehicle-identification-resolve";
import type { SearchVehicleDataResult } from "@/lib/cat/mulesoft-vehicle-search";
import { prisma } from "@/prisma";
import type { Draft } from "@/prisma/generated/client";
import { Prisma } from "@/prisma/generated/client";
import type { Session } from "next-auth";
import { canAccessDraft } from "@/lib/cat/draft-access";
import {
  assertDraftLockForSave,
  buildDraftLockHolder,
  DraftLockDeniedError,
} from "@/lib/cat/draft-lock";
import {
  retailLogDraftCreate,
  retailLogDraftUpdate,
} from "@/lib/cat/draft-activity-log";
import { resolveRetailDraftStepIndexAfterSave } from "@/lib/cat/draft-step-indices";

const searchVehicleSchema = z
  .object({
    chassisNumber: z
      .string()
      .trim()
      .length(17, "Le VIN doit obligatoirement contenir 17 caractères.")
      .regex(
        /^[a-zA-Z0-9]{17}$/,
        "Le numéro de Chassis doit être une suite de 17 caractères alpha-numériques.",
      )
      .optional(),
    orderNumber: z
      .string()
      .min(1)
      .regex(/^\d{6}/)
      .optional(),
    orderYear: z
      .string()
      .trim()
      .min(1)
      .regex(/^\d{4}/)
      .optional(),
  })
  .refine(
    (data) =>
      (!!data.chassisNumber && !data.orderNumber && !data.orderYear) ||
      (!data.chassisNumber && !!data.orderNumber && !!data.orderYear),
    {
      message:
        "Renseignez soit un numéro de chassis, soit un numéro et une année de commande",
    },
  );

const saveDraftDataSchema = z.object({
  id: z.number().int().positive().optional(),
  vin: z.string().trim().max(17).optional(),
  commissionNumber: z.string().trim().min(1).max(6),
  commissionYear: z.string().trim().min(1).max(4),
  owningDealerKvps: z.string().min(1),
  deliveringDealerKvps: z.string().optional(),
  modelDescription: z.string().trim().optional(),
});

export type VehicleIdentificationSearchResult = SearchVehicleDataResult & {
  existingDraft: {
    id: number;
    currentStepIndex: number;
  } | null;
};

async function findExistingDraftForVehicle(
  session: Session,
  params: {
    commissionYear?: string;
    commissionNumber?: string;
    vin?: string | null;
  },
): Promise<{ id: number; currentStepIndex: number } | null> {
  const commissionYear = params.commissionYear?.trim();
  const commissionNumber = params.commissionNumber?.trim();
  const vin = params.vin?.trim() || null;

  let existing: Pick<Draft, "id" | "currentStepIndex" | "kvps"> | null = null;

  if (commissionYear && commissionNumber) {
    existing = await prisma.draft.findFirst({
      where: { commissionYear, commissionNumber },
      select: { id: true, currentStepIndex: true, kvps: true },
    });
  }

  if (!existing && vin) {
    existing = await prisma.draft.findFirst({
      where: { vinNumber: vin },
      select: { id: true, currentStepIndex: true, kvps: true },
    });
  }

  if (!existing || !canAccessDraft(session, existing)) {
    return null;
  }

  return { id: existing.id, currentStepIndex: existing.currentStepIndex };
}

export async function getVehicleData(
  params: z.infer<typeof searchVehicleSchema>,
): Promise<ActionResult<VehicleIdentificationSearchResult>> {
  try {
    const session = await auth();
    if (!session) {
      return { success: false, message: "Non authentifié." };
    }

    const { chassisNumber, orderNumber, orderYear } = validateParams(
      searchVehicleSchema,
      params,
    );

    const brand = session.data?.kvps?.current?.parts?.brand?.trim();
    const outcome = await resolveVehicleIdentificationSearch({
      brand,
      vin: chassisNumber,
      bid: orderNumber && orderYear ? "995" : "",
      commissionNumber: orderNumber,
      commissionYear: orderYear,
    });

    if (outcome.kind === "data") {
      if (outcome.data.brand !== brand) {
        return {
          success: false,
          message:
            "Aucun véhicule correspondant à votre marque n'as été trouvé avec vos critères de recherche.",
        };
      }
      if (
        session.data?.kvps?.current?.parts?.country !== "FRA" &&
        session.data?.kvps?.current?.parts?.wholesalerNumber !== "995" &&
        outcome.data.owningDealerKvps !==
          session.data?.kvps?.current?.identifier
      ) {
        return {
          success: false,
          message:
            "Aucun véhicule correspondant à votre KVPS n'as été trouvé avec vos critères de recherche.",
        };
      }
      if (outcome.data.isAlreadyAssigned) {
        return {
          success: false,
          message: "Le véhicule que vous recherchez est déjà contremarqué.",
        };
      }

      const existingDraft = await findExistingDraftForVehicle(session, {
        commissionYear: outcome.data.commissionYear,
        commissionNumber: outcome.data.commissionNumber,
        vin: outcome.data.vin,
      });

      return {
        success: true,
        data: { ...outcome.data, existingDraft },
      };
    }
    if (outcome.kind === "error") {
      return { success: false, message: outcome.message };
    }

    return {
      success: false,
      message:
        "Renseignez soit un numéro de chassis, soit un numéro et une année de commande.",
    };
  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}

function buildIdentificationDraftData(
  vehicleData: z.infer<typeof saveDraftDataSchema>,
  userId: string,
) {
  const vinNumber = vehicleData.vin?.trim();
  const modelDescription = vehicleData.modelDescription?.trim();

  return {
    kvps: vehicleData.owningDealerKvps,
    vinNumber: vinNumber && vinNumber.length > 0 ? vinNumber : null,
    commissionNumber: vehicleData.commissionNumber,
    commissionYear: vehicleData.commissionYear,
    vehicleModelDescription:
      modelDescription && modelDescription.length > 0 ? modelDescription : null,
    currentStepIndex:
      resolveRetailDraftStepIndexAfterSave("vehicle_identification") ?? 1,
    flowType: "RETAIL",
    createdByUserId: userId,
  };
}

export async function saveDraftData(
  params: SearchVehicleDataResult & { id?: number },
): Promise<ActionResult<Draft>> {
  try {
    const session = await auth();
    if (!session?.user?.id) {
      return { success: false, message: "Non authentifié." };
    }

    const vehicleData = validateParams(saveDraftDataSchema, {
      id: params.id,
      vin: params.vin,
      commissionNumber: params.commissionNumber,
      commissionYear: params.commissionYear,
      owningDealerKvps: params.owningDealerKvps,
      deliveringDealerKvps: params.deliveringDealerKvps,
      modelDescription: params.modelDescription,
    });

    const draftPayload = buildIdentificationDraftData(
      vehicleData,
      session.user.id,
    );
    const sessionKvps =
      session.data?.kvps?.current?.identifier ?? draftPayload.kvps;
    const lockHolder = buildDraftLockHolder(session.user);

    const existingByCommission = await prisma.draft.findFirst({
      where: {
        commissionYear: vehicleData.commissionYear,
        commissionNumber: vehicleData.commissionNumber,
      },
    });

    const vin = draftPayload.vinNumber;
    const existingByVin =
      vin !== null
        ? await prisma.draft.findFirst({
            where: { vinNumber: vin },
          })
        : null;

    let existing = existingByCommission;
    if (!existing && existingByVin) {
      existing = existingByVin;
    }

    if (vehicleData.id) {
      const current = await prisma.draft.findUnique({
        where: { id: vehicleData.id },
      });
      if (!current) {
        return { success: false, message: "Brouillon introuvable." };
      }
      if (!canAccessDraft(session, current)) {
        return { success: false, message: "Accès refusé à ce brouillon." };
      }

      await assertDraftLockForSave(vehicleData.id, lockHolder);

      const savedDraft = await prisma.draft.update({
        where: { id: vehicleData.id },
        data: {
          kvps: draftPayload.kvps,
          vinNumber: draftPayload.vinNumber,
          commissionNumber: draftPayload.commissionNumber,
          commissionYear: draftPayload.commissionYear,
          vehicleModelDescription: draftPayload.vehicleModelDescription,
          currentStepIndex: draftPayload.currentStepIndex,
        },
      });

      await retailLogDraftUpdate({
        userId: session.user.id,
        kvps: sessionKvps,
        draftId: savedDraft.id,
        stepId: "vehicle_identification",
      });

      return { success: true, data: savedDraft };
    }

    if (existing) {
      if (!canAccessDraft(session, existing)) {
        return {
          success: false,
          message: "Un brouillon existe déjà pour ce véhicule.",
        };
      }

      await assertDraftLockForSave(existing.id, lockHolder);

      const savedDraft = await prisma.draft.update({
        where: { id: existing.id },
        data: {
          kvps: draftPayload.kvps,
          vinNumber: draftPayload.vinNumber,
          commissionNumber: draftPayload.commissionNumber,
          commissionYear: draftPayload.commissionYear,
          vehicleModelDescription: draftPayload.vehicleModelDescription,
          currentStepIndex: draftPayload.currentStepIndex,
        },
      });

      await retailLogDraftUpdate({
        userId: session.user.id,
        kvps: sessionKvps,
        draftId: savedDraft.id,
        stepId: "vehicle_identification",
      });

      return { success: true, data: savedDraft };
    }

    const savedDraft = await prisma.draft.create({
      data: draftPayload,
    });

    await assertDraftLockForSave(savedDraft.id, lockHolder);

    await retailLogDraftCreate({
      userId: session.user.id,
      kvps: sessionKvps,
      draftId: savedDraft.id,
    });

    return { success: true, data: savedDraft };
  } catch (error) {
    console.error("[saveDraftData identification]", error);

    if (error instanceof DraftLockDeniedError) {
      return { success: false, message: error.message };
    }

    if (
      error instanceof Prisma.PrismaClientKnownRequestError &&
      error.code === "P2002"
    ) {
      return {
        success: false,
        message: "Un brouillon existe déjà pour ce véhicule.",
      };
    }

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}
